import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, type SxProps } from "@mui/material";
import type { DateTime } from "luxon";
import { type Dispatch, type FC, Fragment, type MouseEvent, type SetStateAction, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { ChangesSource, type OpenShiftRequest, type ReplaceShiftRequest } from "../../../../utils/models";
import { MAX_OPEN_SHIFT_DURATION_HOURS } from "../../../../utils/openShiftDuration";
import { isOpenShiftRequest } from "../../../../utils/openShiftMatching";
import { requestShiftOptionsRole } from "../../../../utils/roles";
import { EditTimePopover } from "../../../EditTimePopover";
import { InitiateRoboCallButton } from "../../../InitiateRoboCallButton/InitiateRoboCallButton";
import { AgencyPostDelayField } from "../../../RequestData/AgencyPostDelayField";
import { useDeleteShift } from "../../../RequestData/hooks";
import { useUpdateRequestTime } from "../../../RequestData/hooks/useUpdateRequestTime";
import { PostToAgencyControl } from "../../../staffing/PostToAgencyControl";
import SystemButton from "../../../UIToolkit/SystemButton/SystemButton";

type Props = {
	isHovered: boolean;
	request: OpenShiftRequest | ReplaceShiftRequest;
	setRequestToShowBroadcastMessagePopup:
		| ((request: OpenShiftRequest | ReplaceShiftRequest) => void)
		| Dispatch<SetStateAction<OpenShiftRequest | ReplaceShiftRequest>>;
	hideActionsWhenNotHovered: boolean;
};

const iconFontSize = 16;
const commonIconSx = {
	fontSize: iconFontSize,
} as const satisfies SxProps;

export const RequestsLeadingContent: FC<Props> = ({ request, setRequestToShowBroadcastMessagePopup, isHovered, hideActionsWhenNotHovered }) => {
	const { track } = useAnalytics();
	const { handleRequestTimeChange } = useUpdateRequestTime();
	const { mutateAsync: deleteShift, isPending: isDeletingShift } = useDeleteShift();
	const [deleteWarningOpen, setDeleteWarningOpen] = useState(false);
	const [isTimePickUpdateLoading, setIsTimePickUpdateLoading] = useState(false);
	const [timePickerAnchorEl, setTimePickerAnchorEl] = useState<null | HTMLElement>(null);
	const timePickerRootElRef = useRef<HTMLElement | null>(null);
	const { t } = useTranslation();

	// Replacement requests cannot use the open-shift time editor or agency delay field.
	const openShiftRequest = isOpenShiftRequest(request) ? request : null;

	const handleConfirmDelete = async () => {
		setDeleteWarningOpen(false);
		await deleteShift({ shiftId: request.id });
	};

	const handleTimePickerClick = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		setTimePickerAnchorEl(timePickerRootElRef.current);
	};

	const handleTimesChange = async ({ newStartTime, newEndTime }: { newStartTime: DateTime; newEndTime: DateTime }) => {
		setIsTimePickUpdateLoading(true);
		await handleRequestTimeChange(request, newStartTime, newEndTime, ChangesSource.DaySchedule);
		setIsTimePickUpdateLoading(false);
	};

	return (
		<Box
			ref={timePickerRootElRef}
			sx={{
				display: "flex",
				flex: 1,
				justifyContent: "end",
				alignItems: "center",
				gap: 0.5,
				opacity: isHovered ? 1 : 0,
				transition: "opacity 0.3s",
			}}
		>
			{(!hideActionsWhenNotHovered || isHovered) && (
				<Fragment>
					<InitiateRoboCallButton requestId={request.id} iconSxOverride={commonIconSx} iconVariant={"filled"} date={request.message.shift_start} />
					<IconButton
						size={"small"}
						aria-label={t("send.message")}
						data-testid={`daily-gaps-request-message-${request.id}`}
						onClick={(event) => {
							event.stopPropagation();
							track("daily_gaps_view_broadcast_message_open", {
								request_id: request.id,
							});
							setRequestToShowBroadcastMessagePopup(request);
						}}
					>
						<ChatOutlinedIcon sx={commonIconSx} />
					</IconButton>
					{openShiftRequest &&
						(isTimePickUpdateLoading ? (
							<CircularProgress size={iconFontSize} />
						) : (
							<IconButton
								size={"small"}
								aria-label={t("gaps.postedSheet.hoursTitle")}
								data-testid={`daily-gaps-request-edit-time-${request.id}`}
								onClick={handleTimePickerClick}
							>
								<AccessTimeFilledIcon sx={commonIconSx} />
							</IconButton>
						))}
					{isDeletingShift ? (
						<CircularProgress size={iconFontSize} />
					) : (
						<IconButton
							size={"small"}
							aria-label={t("delete.request")}
							data-testid={`daily-gaps-request-delete-${request.id}`}
							onClick={(event) => {
								event.stopPropagation();
								setDeleteWarningOpen(true);
							}}
						>
							<DeleteIcon sx={commonIconSx} />
						</IconButton>
					)}
					<PostToAgencyControl request={request} variant="iconToggle" />
				</Fragment>
			)}
			<Dialog open={deleteWarningOpen} onClose={() => setDeleteWarningOpen(false)} sx={{ zIndex: 2000 }}>
				<DialogTitle>{t("delete.open.shift.title")}</DialogTitle>
				<DialogContent>{t("delete.open.shift.warning")}</DialogContent>
				<DialogActions>
					<SystemButton buttonVariant="secondary" onClick={() => setDeleteWarningOpen(false)} disabled={isDeletingShift}>
						{t("cancel")}
					</SystemButton>
					<SystemButton buttonVariant="delete" onClick={handleConfirmDelete} disabled={isDeletingShift} loading={isDeletingShift}>
						{t("delete.request")}
					</SystemButton>
				</DialogActions>
			</Dialog>
			{timePickerAnchorEl && openShiftRequest && (
				<EditTimePopover
					role={requestShiftOptionsRole(request.message.roles)}
					jobId={request.job_id}
					startTime={request.message.shift_start}
					endTime={request.message.shift_end}
					onTimeChange={handleTimesChange}
					onClose={() => setTimePickerAnchorEl(null)}
					anchorEl={timePickerAnchorEl}
					isActual={false}
					extraContent={<AgencyPostDelayField request={openShiftRequest} />}
					maximumDurationHours={MAX_OPEN_SHIFT_DURATION_HOURS}
				/>
			)}
		</Box>
	);
};
